import './SetupAuditTrailTable.scss';

import { AuditTrailExportButton } from './AuditTrailExportButton';
import { AuditTrailSummarizeButton } from './AuditTrailSummarizeButton';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { DateRangePickerParams } from '@ui/DateRangePicker';
import { getColumns } from './SetupAuditTrailTableColumns';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { SonarText } from '@ui/text';
import { Space } from 'antd';

import { ActionFilter, ClassificationFilter, SectionFilter, SourceNamePrefixFilter } from './filters';
import { AuditTrailEvent, ClassificationLevel, coreActions, SchemaFormat } from '@sonar-software/sonar-core';
import {
	DynamicFilterEntry,
	DynamicQueryOperator,
	DynamicQueryParams,
	SonarV3SortDirection,
	SortByOption
} from '@sonar-software/sonar-ts-types';
import { isEqual, uniqueId } from 'lodash';
import { PaginatedTable, PaginatedTableQueryParams } from '@ui/tables/PaginatedTable';
import React, { useEffect, useRef } from 'react';
import { TableFilter, TableModifier, TableQueryParams } from '@ui/table-modifier';
import { useAction, useAsyncState, useQueryParams } from '@hooks';

const getRowKey = (result: AuditTrailEvent) => `${result.auditDate}-${uniqueId()}`;
const columns = getColumns();

const filters: Array<TableFilter> = [ {
	filterComponent: ActionFilter,
	filterFormat: SchemaFormat.Text,
	filterId: 'Action',
	filterLabel: COPY_SPECIFIC.ACTION
}, {
	filterFormat: SchemaFormat.SalesforceUser,
	filterId: 'User',
	filterLabel: COPY_SPECIFIC.USER
}, {
	filterComponent: SectionFilter,
	filterFormat: SchemaFormat.Object,
	filterId: 'Section',
	filterLabel: COPY_SPECIFIC.SECTION
}, {
	filterFormat: SchemaFormat.SalesforceUser,
	filterId: 'DelegateUser',
	filterLabel: COPY_SPECIFIC.DELEGATE_USER
}, {
	filterComponent: SourceNamePrefixFilter,
	filterFormat: SchemaFormat.Object,
	filterId: 'SourceNamePrefix',
	filterLabel: COPY_SPECIFIC.SOURCE_NAMESPACE_PREFIX
}, {
	filterComponent: ClassificationFilter,
	filterFormat: SchemaFormat.Object,
	filterId: 'Classification',
	filterLabel: COPY_SPECIFIC.CLASSIFICATION
} ];

const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Descending,
		key: 'AuditDate',
		label: COPY_SPECIFIC.DATE
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'Action',
		label: COPY_SPECIFIC.ACTION
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'SourceNamePrefix',
		label: COPY_SPECIFIC.SOURCE_NAMESPACE_PREFIX
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'Section',
		label: COPY_SPECIFIC.SECTION
	}
];

const setupAuditTrailSelectors = createAsyncSelectors((state) => state.core.dataSecurity.setupAuditTrail.get);

export type SetupAuditTrailTableQueryParams = PaginatedTableQueryParams & DateRangePickerParams & DynamicQueryParams & {
	Action?: DynamicFilterEntry<string>;
	Classification?: DynamicFilterEntry<Array<ClassificationLevel>>;
	DelegateUser?: DynamicFilterEntry<Array<string>>;
	Section?: DynamicFilterEntry<string>;
	SourceNamePrefix?: DynamicFilterEntry<string>;
	User?: DynamicFilterEntry<Array<string>>;
};

const convertToDql = (params: SetupAuditTrailTableQueryParams) => queryParamsToDynamicQueryLanguageParams(params, {
	filterParams: {
		Action: {
			operator: DynamicQueryOperator.Like,
			value: (filter) => filter?.value
		},
		Classification: {
			value: (filter) => filter?.value
		},
		DelegateUser: {
			value: (filter) => filter?.value
		},
		Section: {
			operator: DynamicQueryOperator.In,
			value: (filter) => filter?.value
		},
		SourceNamePrefix: {
			operator: DynamicQueryOperator.In,
			value: (filter) => filter?.value
		},
		User: {
			value: (filter) => filter?.value
		},
		start: {
			name: 'AuditDate',
			rangeEnd: 'end'
		}
	}
});

export interface SetupAuditTrailTableProps {
	classificationsFilter: Array<ClassificationLevel>;
	dateRange: DateRangePickerParams;
	onClassificationFilterChange: (classifications: Array<ClassificationLevel>) => void;
}

export const SetupAuditTrailTable: React.FC<SetupAuditTrailTableProps> = ({
	classificationsFilter,
	dateRange,
	onClassificationFilterChange
}) => {
	const getSetupAuditTrailEvents = useAction(coreActions.requestGetCoreSetupAuditTrail);
	const { data, isRequested } = useAsyncState(setupAuditTrailSelectors);

	const updatingRef = useRef(false);

	const { queryParams, updateParams } = useQueryParams<SetupAuditTrailTableQueryParams>({
		defaultParams: {
			sortBy: sortOptions[ 0 ].key,
			sortDirection: sortOptions[ 0 ].direction as SonarV3SortDirection
		},
		onUpdate: (newParams, prev, combined) => {
			const { key, paginationDirection } = newParams;

			const classifcations = combined.Classification?.value ?? [];

			if (!isEqual(classifcations, classificationsFilter)) {
				onClassificationFilterChange(classifcations);
				updatingRef.current = true;
			}

			const dql = convertToDql({
				...combined,
				key,
				paginationDirection
			});

			getSetupAuditTrailEvents(dql);
		},
		transform: (newParams, prev, combined) => {
			// Recalculated filter string when external filters change.
			combined.filters = convertToDql(combined).filters;

			return combined;
		}
	});

	useEffect(() => {
		if (dateRange.end !== queryParams?.end || dateRange.start !== queryParams?.start) {
			const { start, end } = dateRange;

			updateParams({
				end,
				start
			});
		}
	}, [ dateRange, queryParams, queryParams?.end, queryParams?.start, updateParams ]);

	useEffect(() => {
		if (!updatingRef.current && !isEqual(classificationsFilter, queryParams.Classification?.value)) {
			updateParams({
				Classification: {
					format: SchemaFormat.Object,
					value: classificationsFilter
				}
			});
		}

		updatingRef.current = false;
	}, [ classificationsFilter, queryParams.Classification?.value, updateParams ]);

	const disabled = (data?.results?.length ?? 0) === 0;

	return (
		<div id='setup-audit-trail-table'>
			<TableModifier
				actionSection={
					<SonarText> { COPY_SPECIFIC.CHANGES_COUNT(data?.totalCount ?? 0) } </SonarText>
				}
				convertToDynamicQueryLanguage={ (params) => {
					updateParams(params as TableQueryParams);

					return convertToDql(params as TableQueryParams);
				} }
				extra={
					<Space>
						<AuditTrailExportButton
							disabled={ disabled }
							queryParams={ convertToDql(queryParams) } />
						<AuditTrailSummarizeButton
							disabled={ disabled }
							queryParams={ convertToDql(queryParams) } />
					</Space>
				}
				filterDisplayType='menu'
				filters={ filters }
				filtersString={ queryParams.filters }
				onChange={ updateParams }
				orderString={ queryParams.ordering }
				sortOptions={ sortOptions } />
			<PaginatedTable
				columns={ columns }
				defaultPageSize={ queryParams.count }
				loading={ isRequested }
				onChange={ updateParams }
				resource={ data }
				rowKey={ getRowKey }
				scroll={ {
					scrollToFirstRowOnChange: true,
					x: 'max-content'
				} }
				sortBy={ queryParams.sortBy }
				sortDirection={ queryParams.sortDirection }
				sticky />
		</div>
	);
};
